import { ActionType, createAction, createAsyncAction } from "typesafe-actions";

import { TerritoryLevel } from "@mapmycustomers/shared";
import BoundaryType from "@mapmycustomers/shared/enum/BoundaryType";
import CountryCode from "@mapmycustomers/shared/enum/CountryCode";
import CountryRegion from "@mapmycustomers/shared/enum/CountryRegion";
import { Territory } from "@mapmycustomers/shared/types/entity";
import BoundaryLegend from "@mapmycustomers/shared/types/map/BoundaryLegend";
import PlatformBounds from "@mapmycustomers/shared/types/map/PlatformBounds";
import MapViewState from "@mapmycustomers/shared/types/viewModel/MapViewState";

import { DemographicMetadata } from "@app/types/map/DemographicData";

import { RegionDemographicData } from "./types/BoundariesLayerData";
import BoundaryStatsDetails from "./types/BoundaryStatsDetails";

export const initializeTerritories = createAsyncAction(
  "map/boundaries/initializeTerritories/request",
  "map/boundaries/initializeTerritories/success",
  "map/boundaries/initializeTerritories/failure"
)<void, void, void>();

export const boundariesViewSettingsChanged = createAction("map/boundaries/viewSettingsChanged")<{
  boundaryKey: NonNullable<MapViewState["boundaryKey"]>;
}>();

export const ensureDemographicDataLoaded = createAsyncAction(
  "map/boundaries/ensureDemographicDataLoaded/request",
  "map/boundaries/ensureDemographicDataLoaded/success",
  "map/boundaries/ensureDemographicDataLoaded/failure"
)<
  CountryCode,
  {
    demographicData?: RegionDemographicData;
    demographyDataCountryCode?: CountryCode;
    metadata?: DemographicMetadata;
  },
  void
>();

export const setHighlightedTerritoryId = createAction("map/boundaries/setHighlightedTerritoryId")<
  Territory["id"] | undefined
>();

export const setBoundaryType = createAction("map/boundaries/setBoundaryType")<BoundaryType>();
export const setZoomLevel = createAction("map/boundaries/setZoomLevel")<TerritoryLevel>();

export const fetchBoundaryStats = createAsyncAction(
  "map/boundaries/fetchBoundaryStats/request",
  "map/boundaries/fetchBoundaryStats/success",
  "map/boundaries/fetchBoundaryStats/failure"
)<
  {
    boundaryLegendId?: BoundaryLegend["id"];
    boundaryType: BoundaryType;
    bounds?: PlatformBounds;
    level?: keyof RegionDemographicData;
    resetRanges?: boolean;
  },
  Omit<BoundaryStatsDetails, "hiddenRanges"> & {
    boundaryType: BoundaryType;
  },
  void
>();

export const hideRanges = createAction("map/boundaries/hideRanges")<{
  boundaryType: BoundaryType;
  hiddenRanges: number[]; // index array
}>();

export const setCountryRegions = createAction("map/boundaries/setCountryRegions")<
  CountryRegion[]
>();

export type BoundariesActions = ActionType<
  | typeof boundariesViewSettingsChanged
  | typeof ensureDemographicDataLoaded
  | typeof fetchBoundaryStats
  | typeof hideRanges
  | typeof initializeTerritories
  | typeof setBoundaryType
  | typeof setCountryRegions
  | typeof setHighlightedTerritoryId
  | typeof setZoomLevel
>;
